import packageJson from "../../../../../package.json" with { type: "json" };
import type { ErrorReportInput, SanitizedErrorReport } from "./error-report.type";
import { ReportGuard } from "./report-guard.util.ts";
import {
  buildErrorReportSignature,
  sanitizeErrorContext,
  sanitizeErrorText,
  sanitizeReportUrl,
} from "./sanitize-error-report.util.ts";

const REPORT_ENDPOINT_PATH = "/log/errors/action";
const REPORT_TIMEOUT_MS = 2_500;

const browserReportGuard = new ReportGuard();

let isGlobalCaptureInstalled = false;

/**
 * Browser-generated messages that carry no actionable signal: opaque
 * cross-origin "Script error.", wallet-extension injections, and media
 * AbortErrors raised when playback is legitimately interrupted.
 */
const UNACTIONABLE_MESSAGE_PATTERNS: readonly RegExp[] = [
  /^Script error\.?$/,
  /window\.ethereum/,
  /^The play\(\) request was interrupted/,
  /^The fetching process for the media resource was aborted/,
];

/** True when a sanitized report message is known, unactionable browser noise. */
export function isUnactionableBrowserError(message: string): boolean {
  return UNACTIONABLE_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Purpose: sanitize, dedupe/rate-bound, and fire-and-forget a browser error
 * report to the existing `POST /log/errors/action` contract. Never throws or
 * rejects — a hostile `message`/`stack`/`additionalData` or a failing
 * transport must never affect the user experience or trigger a recursive
 * report.
 *
 * @example
 * reportBrowserError({ message: error.message, stack: error.stack, source: "window-error" });
 */
export async function reportBrowserError(
  input: ErrorReportInput,
): Promise<void> {
  if (typeof window === "undefined") return;

  let acquiredSignature: string | undefined;

  try {
    const message = sanitizeErrorText(asSafeString(input.message));

    if (!message || isUnactionableBrowserError(message)) return;

    const stack = sanitizeErrorText(asSafeString(input.stack), 4_000);
    const signature = buildErrorReportSignature(message, stack, input.source);

    if (!browserReportGuard.tryAcquire(signature)) return;

    acquiredSignature = signature;

    await sendBrowserReport(
      buildReportPayload(message, stack, input.source, input.additionalData),
    );
  } catch {
    console.warn("[error-reporting] failed to report browser error");
  } finally {
    if (acquiredSignature !== undefined) {
      browserReportGuard.release();
    }
  }
}

/**
 * Purpose: install the pre-hydration `window.onerror` and
 * `unhandledrejection` listeners exactly once. Must be called before
 * `hydrateRoot` so errors thrown during hydration are still captured. Each
 * listener body is isolated so a hostile `event.error`/`reason` (throwing
 * getters, poisoned `toString`) can never escape back into the browser's
 * event dispatch or trigger a recursive `unhandledrejection`.
 *
 * @example
 * installBrowserErrorCapture();
 */
export function installBrowserErrorCapture(): void {
  if (typeof window === "undefined" || isGlobalCaptureInstalled) return;

  isGlobalCaptureInstalled = true;

  window.addEventListener("error", (event) => {
    try {
      const message = safeErrorMessage(event?.message, "Uncaught error");

      // React also hands these to `onRecoverableError` (reported with
      // source "hydration" from entry.client); the window copy is a duplicate.
      if (isReactHydrationMismatchMessage(message)) return;

      reportBrowserError({
        message,
        stack: safeErrorStack(event?.error),
        source: "window-error",
      }).catch(() => undefined);
    } catch {
      // A hostile event object must never escape this listener.
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    try {
      const reason = event?.reason;

      reportBrowserError({
        message: safeErrorMessage(
          reason,
          "Unhandled promise rejection",
        ),
        stack: safeErrorStack(reason),
        source: "unhandled-rejection",
      }).catch(() => undefined);
    } catch {
      // A hostile rejection reason must never escape this listener.
    }
  });
}

async function sendBrowserReport(
  payload: SanitizedErrorReport,
): Promise<void> {
  const abortController = new AbortController();
  const timeoutHandle = setTimeout(
    () => abortController.abort(),
    REPORT_TIMEOUT_MS,
  );

  try {
    await fetch(REPORT_ENDPOINT_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: abortController.signal,
      keepalive: true,
    });
  } finally {
    clearTimeout(timeoutHandle);
  }
}

function buildReportPayload(
  message: string,
  stack: string | undefined,
  source: ErrorReportInput["source"],
  additionalData: Record<string, unknown> | undefined,
): SanitizedErrorReport {
  return {
    message,
    stack,
    source,
    url: sanitizeReportUrl(window.location.href),
    userAgent: sanitizeErrorText(navigator.userAgent, 300) ?? "",
    timestamp: Date.now(),
    buildVersion: packageJson.version,
    app: "web",
    additionalData: sanitizeErrorContext(additionalData),
  };
}

const reactHydrationMismatchPattern =
  /Hydration failed because the initial UI does not match|There was an error while hydrating|Text content does not match server-rendered HTML/;

/**
 * Whether a window error is one of React's hydration-mismatch errors, which
 * are already reported through `hydrateRoot`'s `onRecoverableError`.
 *
 * @example
 * isReactHydrationMismatchMessage("Uncaught Error: Hydration failed because the initial UI does not match what was rendered on the server."); // true
 */
export function isReactHydrationMismatchMessage(message: string): boolean {
  return reactHydrationMismatchPattern.test(message);
}

/** Narrow to a string without ever coercing a hostile value. */
function asSafeString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** Extract a safe message from an arbitrary event field or rejection reason. */
function safeErrorMessage(value: unknown, fallback: string): string {
  try {
    if (typeof value === "string" && value.length > 0) return value;

    if (value instanceof Error && typeof value.message === "string") {
      return value.message;
    }

    if (value === null || value === undefined) return fallback;

    return String(value);
  } catch {
    return fallback;
  }
}

/** Extract a safe stack trace from an arbitrary event field, if present. */
function safeErrorStack(value: unknown): string | undefined {
  try {
    if (value instanceof Error && typeof value.stack === "string") {
      return value.stack;
    }

    return undefined;
  } catch {
    return undefined;
  }
}
